/**
 * EvalScenariosPanel Component
 * Evaluation scenario management panel for the Evaluation Center
 * Extracted from EvalScenariosPage for tab-based layout
 */

import { useState, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Plus, Play, TrendingUp, RefreshCw, History, CheckSquare, FolderPlus, X, Download, Upload } from 'lucide-react';
import { useI18n } from '@/i18n';
import { evalScenariosApi } from '@/lib/api/evalScenarios';
import {
  ScenarioList,
  ScenarioFilters,
  CreateScenarioDialog,
  RunEvaluationDialog,
  PromoteChatDialog,
  ScenarioHistoryPanel,
  EvalRunsPanel,
  CreateTestSuiteDialog,
  useEvalScenarios,
  useEvalCategories,
  useAgentTypes,
  useJudgerModels,
  useCreateEvalScenario,
  useUpdateEvalScenario,
  useDeleteEvalScenario,
  useDuplicateEvalScenario,
  usePromoteChat,
  useRunEvaluation,
  useRunBatchEvaluation,
  useEvalRuns,
  useCreateTestSuite,
  useTestSuites,
  useRunTestSuite,
  useEvalTags,
} from '@/features/eval-scenarios';
import type {
  EvalScenario,
  CreateEvalScenarioRequest,
  UpdateEvalScenarioRequest,
  PromoteChatRequest,
  EvalRunResult,
  CreateTestSuiteRequest,
  UpdateTestSuiteRequest,
  TestSuiteRunResult,
} from '@/types/evalScenarios';

export function EvalScenariosPanel() {
  const { messages } = useI18n();
  const t = messages.evalScenarios;
  const queryClient = useQueryClient();

  // State
  const [filters, setFilters] = useState({
    agent_type: '',
    category: '',
    source: '',
    is_active: '',
    search: '',
    tags: [] as string[],
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editScenario, setEditScenario] = useState<EvalScenario | null>(null);
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [runScenario, setRunScenario] = useState<EvalScenario | null>(null);
  const [promoteChatDialogOpen, setPromoteChatDialogOpen] = useState(false);
  const [historyScenario, setHistoryScenario] = useState<EvalScenario | null>(null);
  const [showRunsPanel, setShowRunsPanel] = useState(false);

  // Selection state for test suite creation
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [createSuiteDialogOpen, setCreateSuiteDialogOpen] = useState(false);

  // Export/Import
  const fileInputRef = useRef<HTMLInputElement>(null);

  // API Params
  const apiParams = {
    category: filters.category || undefined,
    source: filters.source || undefined,
    is_active: filters.is_active ? filters.is_active === 'true' : undefined,
  };

  // Queries
  const { data: scenarios = [], isLoading, refetch } = useEvalScenarios(apiParams);
  const { data: categories = [] } = useEvalCategories();
  const { data: availableTags = [] } = useEvalTags();
  const { data: agentTypes = [] } = useAgentTypes();
  const { data: judgerModels = [] } = useJudgerModels();
  const { data: testSuites = [] } = useTestSuites({ is_active: true });

  // Fetch running evaluations to disable run buttons
  const { data: runsData } = useEvalRuns(20, 0, { autoRefreshOnRunning: true });

  // Compute running scenario IDs from active runs
  const runningScenarioIds = useMemo(() => {
    const ids = new Set<string>();
    if (runsData?.runs) {
      for (const run of runsData.runs) {
        if (run.status !== 'running') continue;

        if (run.trigger_type === 'single') {
          // Single runs encode scenario ID prefix in run_id
          const match = run.run_id.match(/single_\d{8}_\d{6}_([a-f0-9]{8})/);
          if (match) {
            const prefix = match[1];
            for (const scenario of scenarios) {
              if (scenario.id.startsWith(prefix)) {
                ids.add(scenario.id);
                break;
              }
            }
          }
        } else {
          // Batch/suite runs: mark all active scenarios as running
          for (const scenario of scenarios) {
            if (scenario.is_active) {
              ids.add(scenario.id);
            }
          }
        }
      }
    }
    return ids;
  }, [runsData?.runs, scenarios]);

  // Check if any batch/suite run is in progress (to disable batch run button)
  const hasBatchRunning = useMemo(() => {
    return runsData?.runs?.some(
      (run) => run.status === 'running' && run.trigger_type !== 'single'
    ) ?? false;
  }, [runsData?.runs]);

  // Mutations
  const createMutation = useCreateEvalScenario();
  const updateMutation = useUpdateEvalScenario();
  const deleteMutation = useDeleteEvalScenario();
  const duplicateMutation = useDuplicateEvalScenario();
  const promoteMutation = usePromoteChat();
  const runSingleMutation = useRunEvaluation();
  const runBatchMutation = useRunBatchEvaluation();
  const createSuiteMutation = useCreateTestSuite();
  const runSuiteMutation = useRunTestSuite();

  // Filter scenarios by search, agent_type, and tags
  const filteredScenarios = scenarios.filter((scenario) => {
    if (filters.agent_type && scenario.agent_type !== filters.agent_type) {
      return false;
    }
    // Tag filter: scenario must have ALL selected tags
    if (filters.tags.length > 0) {
      const scenarioTags = scenario.tags || [];
      if (!filters.tags.every((t) => scenarioTags.includes(t))) {
        return false;
      }
    }
    if (!filters.search) return true;
    const searchLower = filters.search.toLowerCase();
    return (
      scenario.name.toLowerCase().includes(searchLower) ||
      (scenario.description || '').toLowerCase().includes(searchLower) ||
      (scenario.category || '').toLowerCase().includes(searchLower) ||
      (scenario.conversation?.[0]?.content || '').toLowerCase().includes(searchLower) ||
      scenario.tags?.some((tag) => tag.toLowerCase().includes(searchLower))
    );
  });

  // Handlers
  const handleFilterChange = (newFilters: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleCreate = async (data: CreateEvalScenarioRequest | UpdateEvalScenarioRequest) => {
    try {
      await createMutation.mutateAsync(data as CreateEvalScenarioRequest);
      setCreateDialogOpen(false);
    } catch (error) {
      console.error('Failed to create scenario:', error);
    }
  };

  const handleUpdate = async (data: CreateEvalScenarioRequest | UpdateEvalScenarioRequest) => {
    if (!editScenario) return;
    try {
      await updateMutation.mutateAsync({
        scenarioId: editScenario.id,
        data: data as UpdateEvalScenarioRequest,
      });
      setEditScenario(null);
    } catch (error) {
      console.error('Failed to update scenario:', error);
    }
  };

  const handleDelete = async (scenario: EvalScenario) => {
    if (!confirm(t.deleteConfirm)) return;
    try {
      await deleteMutation.mutateAsync(scenario.id);
    } catch (error) {
      console.error('Failed to delete scenario:', error);
    }
  };

  const handleDuplicate = async (scenario: EvalScenario) => {
    try {
      await duplicateMutation.mutateAsync(scenario.id);
    } catch (error) {
      console.error('Failed to duplicate scenario:', error);
    }
  };

  const handleTagsChange = async (scenarioId: string, tags: string[]) => {
    try {
      await updateMutation.mutateAsync({
        scenarioId,
        data: { tags },
      });
    } catch (error) {
      console.error('Failed to update tags:', error);
    }
  };

  const handleOpenRunDialog = (scenario?: EvalScenario) => {
    setRunScenario(scenario || null);
    setRunDialogOpen(true);
  };

  const handleRunSingle = async (scenarioId: string, userId?: string | null, responseLanguage?: string | null): Promise<EvalRunResult> => {
    return await runSingleMutation.mutateAsync({ scenarioId, userId, responseLanguage });
  };

  const handleRunBatch = async (userId?: string | null, responseLanguage?: string | null): Promise<EvalRunResult> => {
    return await runBatchMutation.mutateAsync({ userId, responseLanguage });
  };

  const handleRunSuite = async (suiteId: string, userId?: string | null, responseLanguage?: string | null): Promise<TestSuiteRunResult> => {
    return await runSuiteMutation.mutateAsync({ suiteId, userId, responseLanguage });
  };

  const handlePromoteChat = async (data: PromoteChatRequest) => {
    try {
      await promoteMutation.mutateAsync(data);
      setPromoteChatDialogOpen(false);
    } catch (error) {
      console.error('Failed to promote chat:', error);
    }
  };

  // Selection mode handlers
  const handleToggleSelectionMode = () => {
    if (selectionMode) {
      setSelectionMode(false);
      setSelectedIds(new Set());
    } else {
      setSelectionMode(true);
    }
  };

  const handleSelectionChange = (newSelectedIds: Set<string>) => {
    setSelectedIds(newSelectedIds);
  };

  const handleOpenCreateSuiteDialog = () => {
    if (selectedIds.size > 0) {
      setCreateSuiteDialogOpen(true);
    }
  };

  const handleCreateSuite = async (data: CreateTestSuiteRequest | UpdateTestSuiteRequest) => {
    try {
      await createSuiteMutation.mutateAsync(data as CreateTestSuiteRequest);
      setCreateSuiteDialogOpen(false);
      setSelectionMode(false);
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Failed to create test suite:', error);
    }
  };

  // Export/Import handlers
  const handleExport = async () => {
    try {
      const bundle = await evalScenariosApi.exportScenarios({ source_env: 'local' });
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().slice(0, 10);
      a.download = `eval-scenarios-${date}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export scenarios:', error);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      alert(t.importInvalidFile);
      return;
    }

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const result = await evalScenariosApi.importScenarios(data);
      alert(t.importSuccess
        .replace('{created}', String(result.created))
        .replace('{skipped}', String(result.skipped)));
      // Invalidate scenarios, categories, and tags caches
      queryClient.invalidateQueries({ queryKey: ['eval-scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['eval-categories'] });
      queryClient.invalidateQueries({ queryKey: ['eval-tags'] });
    } catch (error) {
      console.error('Failed to import scenarios:', error);
      alert(t.importFailed);
    } finally {
      // Reset file input so the same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <>
      {/* Actions Bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Button variant="primary" onClick={() => setCreateDialogOpen(true)} className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700">
            <Plus className="w-4 h-4 mr-2" />
            {t.createScenario}
          </Button>
          <Button variant="secondary" onClick={() => setPromoteChatDialogOpen(true)}>
            <TrendingUp className="w-4 h-4 mr-2" />
            {t.promoteChat}
          </Button>
          {/* Selection mode toggle */}
          <Button
            variant={selectionMode ? 'primary' : 'secondary'}
            onClick={handleToggleSelectionMode}
            className={selectionMode ? 'bg-violet-600 hover:bg-violet-700' : ''}
          >
            {selectionMode ? (
              <>
                <X className="w-4 h-4 mr-2" />
                {t.testSuites?.exitSelect || 'Exit Selection'}
              </>
            ) : (
              <>
                <CheckSquare className="w-4 h-4 mr-2" />
                {t.testSuites?.selectForSuite || 'Select for Suite'}
              </>
            )}
          </Button>
          {/* Create Suite button */}
          {selectionMode && selectedIds.size > 0 && (
            <Button
              variant="primary"
              onClick={handleOpenCreateSuiteDialog}
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700"
            >
              <FolderPlus className="w-4 h-4 mr-2" />
              {t.testSuites?.createSuite || 'Create Suite'} ({selectedIds.size})
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            {t.exportScenarios}
          </Button>
          <Button variant="secondary" onClick={handleImportClick}>
            <Upload className="w-4 h-4 mr-2" />
            {t.importScenarios}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            className="hidden"
            onChange={handleImportFile}
          />
          <Button
            variant="secondary"
            onClick={() => setShowRunsPanel(true)}
          >
            <History className="w-4 h-4 mr-2" />
            {t.runsPanel?.title || 'Run History'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {t.refresh}
          </Button>
          <Button
            variant="primary"
            onClick={() => handleOpenRunDialog()}
            disabled={filteredScenarios.length === 0 || hasBatchRunning}
            className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700"
          >
            {hasBatchRunning ? (
              <span className="animate-spin inline-block mr-2">🔄</span>
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            {hasBatchRunning ? (t.running || 'Running...') : t.runAll}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <ScenarioFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          categories={categories}
          agentTypes={agentTypes}
          availableTags={availableTags}
        />
      </div>

      {/* Scenarios List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 bg-white rounded-lg border border-gray-200">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600 mx-auto mb-2" />
            <p className="text-sm text-gray-500">{messages.common.loading}</p>
          </div>
        </div>
      ) : (
        <ScenarioList
          scenarios={filteredScenarios}
          onEdit={setEditScenario}
          onDelete={handleDelete}
          onRun={handleOpenRunDialog}
          onDuplicate={handleDuplicate}
          onViewHistory={setHistoryScenario}
          onTagsChange={handleTagsChange}
          runningScenarioIds={runningScenarioIds}
          selectable={selectionMode}
          selectedIds={selectedIds}
          onSelectionChange={handleSelectionChange}
        />
      )}

      {/* Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500 mb-1">{t.totalScenarios}</p>
          <p className="text-2xl font-bold text-gray-900">{scenarios.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500 mb-1">{t.active}</p>
          <p className="text-2xl font-bold text-violet-600">
            {scenarios.filter((s) => s.is_active).length}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500 mb-1">{t.manual}</p>
          <p className="text-2xl font-bold text-violet-600">
            {scenarios.filter((s) => s.source === 'manual').length}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500 mb-1">{t.chatPromoted}</p>
          <p className="text-2xl font-bold text-indigo-600">
            {scenarios.filter((s) => s.source === 'chat_promoted').length}
          </p>
        </div>
      </div>

      {/* Dialogs */}
      <CreateScenarioDialog
        isOpen={createDialogOpen || !!editScenario}
        onClose={() => {
          setCreateDialogOpen(false);
          setEditScenario(null);
        }}
        onSubmit={editScenario ? handleUpdate : handleCreate}
        scenario={editScenario}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
        categories={categories}
        agentTypes={agentTypes}
        judgerModels={judgerModels}
      />

      <RunEvaluationDialog
        isOpen={runDialogOpen}
        onClose={() => {
          setRunDialogOpen(false);
          setRunScenario(null);
        }}
        onRunSingle={handleRunSingle}
        onRunBatch={handleRunBatch}
        onRunSuite={handleRunSuite}
        scenario={runScenario}
        scenarios={filteredScenarios}
        suites={testSuites}
      />

      <PromoteChatDialog
        isOpen={promoteChatDialogOpen}
        onClose={() => setPromoteChatDialogOpen(false)}
        onSubmit={handlePromoteChat}
        isSubmitting={promoteMutation.isPending}
        categories={categories}
      />

      {/* History Panels */}
      <ScenarioHistoryPanel
        scenarioId={historyScenario?.id || ''}
        scenarioName={historyScenario?.name || ''}
        isOpen={!!historyScenario}
        onClose={() => setHistoryScenario(null)}
      />

      <EvalRunsPanel
        isOpen={showRunsPanel}
        onClose={() => setShowRunsPanel(false)}
      />

      {/* Create Test Suite Dialog */}
      <CreateTestSuiteDialog
        isOpen={createSuiteDialogOpen}
        onClose={() => setCreateSuiteDialogOpen(false)}
        onSubmit={handleCreateSuite}
        selectedScenarioIds={Array.from(selectedIds)}
        isSubmitting={createSuiteMutation.isPending}
      />
    </>
  );
}
